<script>
	import { Button, Col, Row } from "sveltestrap";
	import { form, field, combined } from "svelte-forms";
	import {
		required,
		min,
		max,
		email,
		pattern,
	} from "svelte-forms/validators";
	import ErrorMessageHandler from "../../../components/ErrorMessageHandler/ErrorMessageHandler.svelte";
	import Loader from "../../../components/Loader/Loader.svelte";
	import PageHeader from "../../../components/PageHeader/PageHeader.svelte";
	import client from "../../../services/client";
	import { errorMessage, loading } from "../../../store/store";
	import { navigate } from "svelte-routing";
	import { onMount } from "svelte";
	import { extractErrorMessage } from "src/utils/misc";
	import CustomSearch from "../../../components/CustomSearch/CustomSearch.svelte";
	import CustomTextArea from "../../../components/CustomTextArea/CustomTextArea.svelte";
	import CustomCheckboxGroup from "../../../components/CustomCheckboxGroup/CustomCheckboxGroup.svelte";
	import CustomInput from "../../../components/CustomInput/CustomInput.svelte";
	import CustomCollapseCard from "../../../components/CustomCollapseCard/CustomCollapseCard.svelte";
	import { filedrop } from "filedrop-svelte";
  	import CustomFileInput from "../../../components/CustomFileInput/CustomFileInput.svelte";
	import { debounce } from 'lodash';

	export let isEdit;

	// form fields
	const vehicleNumber = field("vehicleNumber", "", [required()]);
	const assetsOwner = field("assetsOwner", "", [required()]);
	const carMake = field("carMake", "", [required()]);
	const selectedSides = field("selectedSides", [], [required()]); // maps to FinalDamagedSides
	const carModel = field("carModel", "", [required()]);
	const damageCause = field("damageCause", "", [required()]); // maps to BookingNo
	const finalAmount = field("finalAmount", "", [required()]);
	const remarks = field("remarks", "", [required()]);

	// file arrays
	let workshopInvoiceFiles = [];
	let additionalPhotoFiles = [];

	let load = false;
	loading.subscribe((value) => {
		load = value;
	});

	let err;
	errorMessage.subscribe((value) => {
		err = value;
	});

	let options = [
		{ label: "Front", value: "front" },
		{ label: "Driver Side", value: "driverSide" },
		{ label: "Back", value: "back" },
		{ label: "Passenger Side", value: "passengerSide" },
	];

	const myForm = form(
		vehicleNumber,
		assetsOwner,
		carMake,
		carModel,
		selectedSides,
		damageCause,
		finalAmount,
		remarks
	);

	let vehicleId = null;
	let selectedVehicleOption = null;

	// when vehicle selected from dropdown, get other vehicle details (e.g. asset owner)
	$: if (selectedVehicleOption) {
		onVehicleSelect(selectedVehicleOption);
	}

	// load all carplates based on user input
	async function loadVehicleOptions(inputValue) {
		// only search if input has at least 3 characters
		if (!inputValue || inputValue.trim().length < 3) return [];
		try {
			const res = await client(
				`/v1/plateNumbers/${inputValue}?limit=100`,
				{ method: "GET", version: "Vehicle" }
		);
		return res.data.map(car => ({ value: car.vehicleId, label: car.plateNo }));
		} catch (e) {
			console.error(e);
		return [];
		}
	}

	const vehicleOptions = debounce(
		loadVehicleOptions,
		300,
		{ leading: true, trailing: true }
	);

	// when vehicle is selected, fetch other vehicle details
	async function onVehicleSelect(option) {
		vehicleId = option.value;
		vehicleNumber.set(option.label);
		try {
			const { data } = await client(
				`/v1/${vehicleId}/details`,
				{ method: "GET", version: "Vehicle" }
			);

			// populate form fields with vehicle details
			assetsOwner.set(data.assetOwnerName || "");
			carMake.set(data.vehicleMakeName || "");
			carModel.set(data.vehicleModelName || "");
		} catch (e) {
			console.error(e);
			extractErrorMessage(e);
			assetsOwner.set("");
			carMake.set("");
			carModel.set("");
		}
	}

  	function onWorkshopFilesSelected(e) {
		const newFiles = Array.from(e.target.files)
		workshopInvoiceFiles = [...workshopInvoiceFiles, ...newFiles]
	}	

	function onAdditionalFilesSelected(e) {
		const newFiles = Array.from(e.target.files)
		additionalPhotoFiles = [...additionalPhotoFiles, ...newFiles]
	}

	// remove by index
	function removeWorkshopFile(idx) {
		workshopInvoiceFiles.splice(idx, 1);
		workshopInvoiceFiles = [...workshopInvoiceFiles];
	}
	function removeAdditionalPhoto(idx) {
		additionalPhotoFiles.splice(idx, 1);
		additionalPhotoFiles = [...additionalPhotoFiles];
	}

	// handle create case submission
	async function handleSubmit() {
		if (!myForm.validate()) return;
		try {
			const formData = new FormData();
			formData.append('VehicleId', vehicleId);
			formData.append('BookingNo', $damageCause.value);
			formData.append('FinalAmount', parseFloat($finalAmount.value).toFixed(2));
			formData.append('Remarks', $remarks.value);
			$selectedSides.value.forEach((side, idx) => {
				formData.append(`FinalDamagedSides[${idx}].side`, side);
			});
			workshopInvoiceFiles.forEach(file =>
				formData.append('WorkshopInvoice', file, file.name)
			);
			additionalPhotoFiles.forEach(file =>
				formData.append('AdditionalPhotos', file, file.name)
			);

			const res = await client(
				'/v1/CaseManagement/case/manual',
				{ method: 'POST', version: 'Tasking', data: formData }
			);
			if (res.status === 204) {
				navigate('/issues/case-management?tab=3');
			}
		} catch (err) {
			console.error(err);
			extractErrorMessage(err);
		}
	}
</script>

<Row>
	<Col>
		<ErrorMessageHandler message={err} />
		<div>
			<PageHeader title={`Create New Case`} />

			<!-- #region vehicle information -->
			<CustomCollapseCard
				title="Vehicle Information"
				isOpenDefault={true}
				customClass="mb-4"
			>
				<Col xs={6}>
					<Row class="mb-4">
						<Col>
							<Row>
								<Col>
									<label for="vehicleNumber" class="d-block">
										<span>
											Vehicle No<span class="text-danger"
												>*</span
											>
										</span>
									</label>
								</Col>
							</Row>
							<Row>
								<Col>
									<CustomSearch
									bind:value={selectedVehicleOption}
									loadOptions={vehicleOptions}
									placeholder="Enter vehicle no"
									error={$myForm.hasError('vehicleNumber.required')}
									errorText="Vehicle no is required"
									/>
								</Col>
							</Row>
						</Col>
						<Col>
							<Row>
								<Col>
									<label for="email" class="d-block">
										<span>
											Asset Owner<span class="text-danger"
												>*</span
											>
										</span>
									</label>
								</Col>
							</Row>
							<Row>
								<Col>
									<CustomInput
										type="text"
										disabled={true}
										placeholder="Enter Asset Owner"
										bind:value={$assetsOwner.value}
										error={$myForm.hasError(
											"assetsOwner.required"
										)}
									/>
								</Col>
							</Row>
						</Col>
					</Row>
					<Row>
						<Col>
							<Row>
								<Col>
									<label for="email" class="d-block">
										<span>
											Make<span class="text-danger"
												>*</span
											>
										</span>
									</label>
								</Col>
							</Row>
							<Row>
								<Col>
									<CustomInput
										type="text"
										disabled={true}
										placeholder="Enter Make"
										bind:value={$carMake.value}
										error={$myForm.hasError(
											"carMake.required"
										)}
									/>
								</Col>
							</Row>
						</Col>
						<Col>
							<Row>
								<Col>
									<label for="email" class="d-block">
										<span>
											Model<span class="text-danger"
												>*</span
											>
										</span>
									</label>
								</Col>
							</Row>
							<Row>
								<Col>
									<CustomInput
										type="text"
										disabled={true}
										placeholder="Enter model"
										bind:value={$carModel.value}
										error={$myForm.hasError(
											"carModel.required"
										)}
									/>
								</Col>
							</Row>
						</Col>
					</Row>
				</Col>
			</CustomCollapseCard>
			<!-- #endregion -->

			<!-- #region damage information -->
			<CustomCollapseCard
				customClass="mb-4"
				title="Damage Information"
				isOpenDefault={true}
			>
				<Col xs={6}>
					<Row class="mb-4">
						<Col xs={6}>
							<Row>
								<Col>
									<label for="email" class="d-block">
										<span>
											Who caused the damage?<span
												class="text-danger">*</span
											>
										</span>
									</label>
								</Col>
							</Row>
							<Row>
								<Col>
									<CustomInput
										loadOptions={options}
										id={`damageCause`}
										type="text"
										bind:value={$damageCause.value}
										placeholder="Enter booking ID"
										error={$myForm.hasError(
											"damageCause.required"
										)}
									/>
								</Col>
							</Row>
						</Col>
					</Row>
				</Col>
			</CustomCollapseCard>
			<!-- #endregion -->

			<!-- #region quotation -->
			<CustomCollapseCard
				title="Quotation"
				isOpenDefault={true}
				customClass="mb-4"
			>
				<Col xs={6}>
					<Row class="mb-4">
						<Col>
							<Row>
								<Col>
									<label for="email" class="d-block">
										<span>
											Where is the damage?<span
												class="text-danger">*</span
											>
										</span>
									</label>
								</Col>
							</Row>
							<Row>
								<Col>
									<CustomCheckboxGroup
										{options}
										bind:selected={$selectedSides.value}
									/>
								</Col>
							</Row>
						</Col>
					</Row>
					<Row class="mb-4">
						<Col xs={6}>
							<Row>
								<Col>
									<label for="email" class="d-block">
										<span>
											Final Amount<span
												class="text-danger">*</span
											>
										</span>
									</label>
								</Col>
							</Row>
							<Row>
								<Col>
									<CustomInput
										type="text"
										placeholder="Enter final amount"
										bind:value={$finalAmount.value}
										error={$myForm.hasError(
											"finalAmount.required"
										)}
									/>
								</Col>
							</Row>
						</Col>
					</Row>
					<Row>
						<Col>
							<Row>
								<Col>
									<label for="email" class="d-block">
										<span>
											Remarks<span class="text-danger"
												>*</span
											>
										</span>
									</label>
								</Col>
							</Row>
							<Row>
								<Col xs={8}>
									<textarea
										type="text"
										bind:value={$remarks.value}
										style="height: 150px;"
										placeholder="Enter remarks"
										class="form-control getgo input"
									/>
								</Col>
							</Row>
						</Col>
					</Row>
				</Col>
			</CustomCollapseCard>
			<!-- #endregion -->

			<!-- Others Card -->
			<CustomCollapseCard title="Others" isOpenDefault={true} customClass="mb-4">
				<Col xs={6}>

					<!-- Workshop Quotation -->
					<CustomFileInput
					title="Workshop Quotation"
					fileType="file"
					name="workshopInvoice"
					files={workshopInvoiceFiles}
					handleFileChange={onWorkshopFilesSelected}
					on:remove={({ detail }) => removeWorkshopFile(detail.index)}
					/>

					<!-- Additional Photos -->
					<CustomFileInput
					title="Additional Photos"
					fileType="image"
					name="additionalPhotos"
					files={additionalPhotoFiles}
					handleFileChange={onAdditionalFilesSelected}
					on:remove={({ detail }) => removeAdditionalPhoto(detail.index)}
					/>

				</Col>
			</CustomCollapseCard>
			<Row>
				<Col class="d-flex justify-content-end gap-3">
					<Button class="themeButton">Back</Button>
					<Button
						class="themeButton primary"
						type="button"
						on:click={handleSubmit}
						disabled={!$vehicleNumber.value || !$assetsOwner.value || !$carMake.value ||
								!$carModel.value || !$damageCause.value || !$finalAmount.value || !$remarks.value 
								|| !$selectedSides.value.length}
					>
						Create
					</Button>
				</Col>
			</Row>
			<!-- #endregion -->
		</div>
	</Col>
</Row>